import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    TicketDTO,
    useSearchTicketsApi,
} from '../../../api/ticket-api';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { T } from '../../../components/basic/text';
import { calendar } from '../../../components/basic/calendar';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { ManageTicketModal } from './manage-ticket-modal';

export function TicketApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<TicketDTO | undefined>();
    const [statusFilter, setStatusFilter] = useState<string>('');
    const ticketInit = useSearchTicketsApi();

    const { result, error, busy, initialized, initializing } = ticketInit;

    const columns: IBasicTableColumn<TicketDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 60,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>user</T>,
                name: 'userName',
                width: 150,
            },
            {
                Header: <T>subject</T>,
                name: 'subject',
                minWidth: 200,
            },
            {
                Header: <T>category</T>,
                name: 'category',
                width: 120,
            },
            {
                Header: <T>priority</T>,
                name: 'priority',
                width: 100,
                Cell: ({ row }) => {
                    const color =
                        row.priority === 'Urgent'
                            ? 'danger'
                            : row.priority === 'High'
                            ? 'warning'
                            : 'info';
                    return (
                        <bd.Badge variant={color} size="sm">
                            {row.priority}
                        </bd.Badge>
                    );
                },
            },
            {
                Header: <T>status</T>,
                name: 'status',
                width: 120,
                Cell: ({ row }) => {
                    const color =
                        row.status === 'Open'
                            ? 'primary'
                            : row.status === 'InProgress'
                            ? 'warning'
                            : row.status === 'Resolved'
                            ? 'success'
                            : 'secondary';
                    return (
                        <bd.Badge variant={color} size="sm">
                            {row.status}
                        </bd.Badge>
                    );
                },
            },
            {
                Header: <T>created-at</T>,
                name: 'createdAt',
                width: 180,
                Cell: ({ row }) => (
                    <span dir="ltr">{row.createdAt ? calendar.dateToStringHHMM(row.createdAt) : '-'}</span>
                ),
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        ticketInit.post(null, { status: statusFilter });
    }, [initialized, initializing, error, ticketInit, statusFilter]);

    const handleFilterChange = (status: string) => {
        setStatusFilter(status);
        ticketInit.post(null, { status: status || undefined });
    };

    if (error) return <InitFailed ex={error} onRetry={() => ticketInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="tickets-management"
                    controls={
                        <bd.ButtonGroup size="sm">
                            <bd.Button
                                variant={statusFilter === '' ? 'contained' : 'outline'}
                                onClick={() => handleFilterChange('')}
                            >
                                <T>all</T>
                            </bd.Button>
                            <bd.Button
                                variant={statusFilter === 'Open' ? 'contained' : 'outline'}
                                onClick={() => handleFilterChange('Open')}
                            >
                                <T>open</T>
                            </bd.Button>
                            <bd.Button
                                variant={statusFilter === 'InProgress' ? 'contained' : 'outline'}
                                onClick={() => handleFilterChange('InProgress')}
                            >
                                <T>in-progress</T>
                            </bd.Button>
                            <bd.Button
                                variant={statusFilter === 'Resolved' ? 'contained' : 'outline'}
                                onClick={() => handleFilterChange('Resolved')}
                                >
                                <T>resolved</T>
                            </bd.Button>
                            <bd.Button
                                variant={statusFilter === 'Closed' ? 'contained' : 'outline'}
                                onClick={() => handleFilterChange('Closed')}
                            >
                                <T>closed</T>
                            </bd.Button>
                        </bd.ButtonGroup>
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        cellCSS={({ row }) => {
                            if (row.status === 'Closed') return 'bg-gray-100 opacity-75';
                            if (row.priority === 'Urgent') return 'bg-red-50';
                            return '';
                        }}
                        columns={columns}
                        data={result.tickets || []}
                        onRowClick={({ row }) => {
                            setSelectedTicket(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <ManageTicketModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedTicket(undefined);
                        }}
                        selectedTicket={selectedTicket}
                        onUpdate={() => ticketInit.post(null, { status: statusFilter })}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}

export default TicketApp;

